import { TrendingDown, TrendingUp, ShieldAlert, AlertCircle } from 'lucide-react';
import type { RiskConditionChip } from '@/api/types';
import { cn } from '@/lib/cn';
import { num, pct } from '@/lib/format';

const CONFIG: Record<string, { label: string; tone: string; icon: typeof ShieldAlert }> = {
  STOP_LOSS:        { label: 'Stop',  tone: 'red',    icon: TrendingDown },
  TAKE_PROFIT:      { label: 'TP',    tone: 'green',  icon: TrendingUp },
  MARKET_DROP:      { label: 'Mkt',   tone: 'amber',  icon: AlertCircle },
  POSITION_LIMIT:   { label: 'Pos',   tone: 'amber',  icon: ShieldAlert },
  DAILY_LOSS_LIMIT: { label: 'Loss',  tone: 'red',    icon: ShieldAlert },
  VOLATILITY_HALT:  { label: 'Vol',   tone: 'amber',  icon: AlertCircle },
};

const TONE: Record<string, string> = {
  red:    'bg-kam-red-soft text-kam-red border-kam-red/25',
  green:  'bg-kam-green-soft text-kam-green border-kam-green/25',
  amber:  'bg-kam-amber-soft text-kam-amber border-kam-amber/30',
};

/**
 * Compact chip for an active risk condition. Shows the threshold + how far
 * the current price is from firing (when applicable).
 */
export function RiskChip({ chip }: { chip: RiskConditionChip }) {
  const cfg = CONFIG[chip.type] ?? { label: chip.type, tone: 'amber', icon: ShieldAlert };
  const Icon = cfg.icon;
  const distance = chip.distancePct;

  const title = [
    chip.type,
    chip.action && `→ ${chip.action}`,
    chip.thresholdValue != null && `@ ${num(chip.thresholdValue)}`,
    distance != null && `(${pct(distance)} away)`,
    chip.triggered && '⚠ TRIGGERED',
  ].filter(Boolean).join(' ');

  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-[10px] font-semibold border whitespace-nowrap',
        TONE[cfg.tone] ?? TONE.amber,
        chip.triggered && 'ring-1 ring-current',
      )}
    >
      <Icon size={10} strokeWidth={2.5} />
      <span>{cfg.label}</span>
      {chip.thresholdValue != null && (
        <span className="font-mono opacity-90">{num(chip.thresholdValue)}</span>
      )}
      {distance != null && (
        <span className="font-mono opacity-70">({pct(distance)})</span>
      )}
    </span>
  );
}
